import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { isNullOrUndefined, isEmpty } from "./";

/**
 * Merges class names using clsx and tailwind-merge
 * @param inputs - Class names to merge
 * @returns Merged class names string
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isAppleUser(mobileOnly = false) {
  if (!isWindow()) return true;
  const userAgent = navigator?.userAgent;
  const isIos = /iPhone|iPad|iPod|Mac/i.test(userAgent);

  const isMac =
      /MacIntel/i.test(userAgent) &&
      navigator.platform.indexOf("Mac") > -1 &&
      !mobileOnly;

  return isIos || isMac;
}

export function isWindow() {
    return typeof window !== "undefined";
}

/**
 * Validates and cleans URL fields for API requests
 * @param url - URL string to validate and clean
 * @returns Valid URL string or undefined
 */
export function cleanUrl(url?: string): string | undefined {
  if (isNullOrUndefined(url) || isEmpty(url)) return undefined;
  const trimmed = url.trim();
  // Basic URL validation - must start with http:// or https://
  if (!/^https?:\/\/.+/.test(trimmed)) {
    return undefined;
  }
  return trimmed;
}

/**
 * Copies text to clipboard with visual feedback
 * @param text - Text to copy to clipboard
 * @param buttonElement - Button element to provide visual feedback
 * @returns Promise that resolves when text is copied
 */
export async function copyToClipboard(text: string, buttonElement?: HTMLButtonElement): Promise<boolean> {
  try {
    if (!text) return false;
    
    await navigator.clipboard.writeText(text);
    
    // Provide visual feedback if button element is provided
    if (buttonElement) {
      buttonElement.classList.add('text-green-600');
      setTimeout(() => buttonElement.classList.remove('text-green-600'), 600);
    }
    
    return true;
  } catch (error) {
    console.error('Failed to copy text to clipboard:', error);
    return false;
  }
}

// Tag color system - predefined colors for consistent styling
const TAG_COLORS = [
  "bg-blue-100 text-blue-800 border-blue-200",
  "bg-green-100 text-green-800 border-green-200", 
  "bg-purple-100 text-purple-800 border-purple-200",
  "bg-pink-100 text-pink-800 border-pink-200",
  "bg-indigo-100 text-indigo-800 border-indigo-200",
  "bg-red-100 text-red-800 border-red-200",
  "bg-yellow-100 text-yellow-800 border-yellow-200",
  "bg-teal-100 text-teal-800 border-teal-200",
  "bg-cyan-100 text-cyan-800 border-cyan-200",
  "bg-emerald-100 text-emerald-800 border-emerald-200",
  "bg-violet-100 text-violet-800 border-violet-200",
  "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200",
  "bg-rose-100 text-rose-800 border-rose-200",
  "bg-sky-100 text-sky-800 border-sky-200",
  "bg-lime-100 text-lime-800 border-lime-200",
] as const;

/**
 * Hash function to consistently assign colors to tags
 * @param tagName - The name of the tag
 * @returns CSS classes for consistent tag coloring
 */
export function getTagColor(tagName: string): string {
  // Simple hash function for consistent color assignment
  let hash = 0;
  for (let i = 0; i < tagName.length; i++) {
    const char = tagName.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // Get positive index within color array bounds
  const colorIndex = Math.abs(hash) % TAG_COLORS.length;
  return TAG_COLORS[colorIndex];
}

/**
 * Generates a URL-friendly slug from a given string
 * @param text - The text to convert to a slug
 * @returns URL-friendly slug string
 */
export function generateSlug(text: string): string {
  if (!text || !text.trim()) return "";
  
  return text
    .toLowerCase()
    .trim()
    // Replace spaces and special characters with hyphens
    .replace(/[^a-z0-9]+/g, "-")
    // Remove leading and trailing hyphens
    .replace(/(^-|-$)/g, "");
}

